import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
// V4.0: the DOCX-native editor. Code-split — this whole container is React.lazy'd
// by EditorSwitch, so the bundle only loads for docs flagged editor_engine='docx_native'.
import { DocxEditor, type DocxEditorRef, type EditorMode, buildCheckboxNode } from '@propdocs-editor/react';
import '@propdocs-editor/react/styles.css';
// Re-points the editor's --doc-* tokens at PropDocs brand values. MUST come
// after the package stylesheet so these overrides win.
import '../../../../styles/docx-editor-brand.css';
import { useAuth } from '../../../../auth';
import { queriesV2, mutations } from '../../../../api';
import { usePdfExport } from '../../../../contexts/PdfExportContext';
import { useNotifications } from '../../../../notifications';
import { updateDocument } from '../../../../utils/api/update-document-offer/update-document';
import { getDocumentById } from '../../../../utils/api/get-document-by-id';
import { documentStateFromResponse } from '../../../../utils/document-wizard-utils/document-state-from-response';
import { useDocumentState } from '../../../../contexts/DocumentContext';
import { useDeal } from '../../../../contexts/DealContext';
import { useDocumentRole } from '../../../../shared/hooks/useDocumentRole/useDocumentRole';
import type { DocumentEditorContainerPropTypes } from '../utils/document-editor.types';
import { useResolvedDocxBody } from './useResolvedDocxBody';
import { useImportReload } from './useImportReload';
import { DownloadPdfModal, type PdfAttachment } from './DownloadPdfModal';
import { toDocxEditorStatus, isDealDocumentEditable } from './dealDocumentStatus';
import { makeDealSuggestionGate, type DealSuggestionRow } from './dealSuggestionGate';
import { dealEditorCapabilities } from './dealEditorCapabilities';
import { useDocxSuggestionSync } from './useDocxSuggestionSync';
import { useDocxPrivateCommentSync } from './useDocxPrivateCommentSync';
import { useLazyDocxMigration } from './useLazyDocxMigration';
import { encodeAuthorToken } from './authorToken';
import { registerDocxNativeEditor, getDocxNativeEditor, makeHandleFromRef } from './docxNativeEditorRegistry';
import { useDealPlaceholderBridge } from './useDealPlaceholderBridge';
import { restrictedEditingPlugin, setRestrictedContext, isCursorInsertable } from './plugins/restrictedEditing';
import { placeholderSyncPlugin } from './plugins/placeholderSync';
import { CLAUSE_DND_MIME, decodeClauseDnd, type ClauseDndPayload } from './clauseDnd';
import type { EditorView } from 'prosemirror-view';

const AUTOSAVE_DEBOUNCE_MS = 1500;

type DocxEditorPropsT = React.ComponentProps<typeof DocxEditor>;
type EditorDocument = DocxEditorPropsT['document'];
type EditorDocumentBuffer = DocxEditorPropsT['documentBuffer'];

/**
 * DOCX-native DEAL-DOCUMENT editor container — the V4.0 replacement for
 * `DocumentEditorContainer` (CKEditor). Satisfies the same prop contract so the
 * deal page can swap it in behind `editor_engine`, with the surrounding deal
 * chrome (header, sidebars, signing flow) unchanged.
 *
 * How this differs from the TEMPLATE container:
 *  - The relational tables stay AUTHORITATIVE. `document_suggestions` /
 *    `document_comments` remain the source of truth for tracked changes and
 *    comments; the editor's native changes carry the row's `external_id` in the
 *    OOXML author token (see ./authorToken) so each change re-links exactly.
 *  - Editing is role- and status-driven (not pinned to suggesting like
 *    templates): the counterparty always works in suggesting mode, and the whole
 *    surface locks once the doc leaves the editable states.
 *  - Accept/reject is gated by `decideSuggestion` via ./dealSuggestionGate.
 */
export const DocxNativeDocumentEditorContainer = ({
  document: documentProp,
  documentId,
  teamId,
  canEdit,
  canComment,
  hasCurrentTeamPossession,
  documentData,
  documentLoading,
  editorMode = 'edit',
  onChange,
  onCompareVersions,
  onAutosaveError,
  onSaved,
}: DocumentEditorContainerPropTypes & {
  /** Opens the page-level Compare view (File → "Compare versions…"). Rendered by
   *  the PAGE in place of the editor, matching the template's compare flow. */
  onCompareVersions?: () => void;
  /** Notified when an autosave attempt throws, so the page can warn the user
   *  their changes aren't being persisted instead of failing silently. */
  onAutosaveError?: (err: unknown) => void;
  /** Fired after each SUCCESSFUL autosave so the page can advance its
   *  "Last Saved" clock. The `lastUpdated` value from the document query is
   *  Apollo-cached and does NOT refresh on autosave, so without this the
   *  header timestamp looks frozen even though saves are landing. */
  onSaved?: () => void;
}) => {
  const editorRef = useRef<DocxEditorRef>(null);
  /** True once the editor has rendered — see the mount gate near the return. */
  const hasMountedRef = useRef(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const { startPdfPolling } = usePdfExport();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications() as any;
  const [exportPdfMutation, { loading: isExportingPdf }] = useMutation(mutations.exportDocumentPdf);
  const apolloClient = useApolloClient();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const dirtyAgainRef = useRef(false);
  // Last pending-suggestion count we pushed to DocumentContext — only refresh
  // the deal state (which drives the send-dropdown gate) when it actually moves.
  const lastSuggCountRef = useRef<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [{ user }] = useAuth() as any;
  const { dealId } = useDeal();
  const {
    state: { status: documentStatus },
    updateDocumentState,
  } = useDocumentState();
  const { isDocumentCreator } = useDocumentRole(documentId!);

  const doc = documentData?.getDocumentById as Record<string, any> | undefined;

  // ── Editor configuration inherited from the template ──────────────────────
  // A deal created from a template copies its editor configuration (restricted
  // editing, header/footer locks, Word-export block). These must be honored in
  // the deal exactly as they are in the template editor — they're the template
  // author's authoring contract, and the deal is where it actually matters.
  const editorConfig = (doc?.editorConfiguration ?? {}) as Record<string, any>;
  const isRestrictedEditingEnabled = editorConfig.restricted_editing === true;
  const disableWordExport = editorConfig.disable_word_export === true;
  // H/F locks are INDEPENDENT of restricted editing (same rule as the template
  // editor, and the same rule CKEditor had): `header_restricted_editing` /
  // `footer_restricted_editing` are their own flags, settable on any template.
  // Gating them on `restricted_editing` made a lock set on a non-restricted
  // template silently not bite in the deal document.
  const headerLocked = editorConfig.header_restricted_editing === true;
  const footerLocked = editorConfig.footer_restricted_editing === true;

  // ── Identity + role ───────────────────────────────────────────────────────
  // The author string carries the acting user's id so every tracked change this
  // session mints can be re-linked to its `document_suggestions` row. The row's
  // `external_id` is stamped in by the sync layer once the row exists; until
  // then the id half alone still identifies the author unambiguously.
  const displayName: string = user?.fullName || user?.email || 'User';
  const author = useMemo(
    () => encodeAuthorToken({ authorId: user?.id ? String(user.id) : undefined, display: displayName }),
    [user?.id, displayName]
  );

  // Project this deal's live tracked changes into `document_suggestions` so the
  // send-for-signing gate + notification counts see them (the body alone is
  // invisible to those backend checks). See useDocxSuggestionSync.
  // Seed the sync with external_ids that ALREADY exist so it never re-creates
  // them (which fails the unique-external_id validation + spams the console).
  const existingSuggestionIds = useMemo(
    () =>
      ((doc?.documentSuggestions ?? []) as Array<Record<string, any>>)
        .map((s) => (s?.externalId != null ? String(s.externalId) : null))
        .filter((v): v is string => v != null),
    [doc?.documentSuggestions]
  );
  const suggestionSync = useDocxSuggestionSync(
    documentId ? String(documentId) : undefined,
    'Document',
    existingSuggestionIds
  );

  // Private comments are the mirror case: their TEXT must never sit in the
  // shared body, so it is mirrored to the access-controlled comments table and
  // restored on load for the users the server authorizes.
  const privateCommentSync = useDocxPrivateCommentSync({
    documentId: documentId ? String(documentId) : undefined,
    enabled: canComment,
  });

  const actorRole: 'creator' | 'receiver' | 'lost' = useMemo(() => {
    if (doc?.currentUserRole === 'receiver') return 'receiver';
    if (isDocumentCreator || doc?.currentUserRole === 'creator') return 'creator';
    return 'lost';
  }, [doc?.currentUserRole, isDocumentCreator]);

  // ── Lifecycle lock ────────────────────────────────────────────────────────
  // Signing/executed/terminated lock the document (editor empties the toolbar and
  // goes read-only). Possession + explicit canEdit still gate the editable states.
  const lifecycleStatus = toDocxEditorStatus(documentStatus);
  const statusAllowsEditing = isDealDocumentEditable(documentStatus);
  const readOnly = !statusAllowsEditing || !canEdit || !hasCurrentTeamPossession || editorMode === 'compare';

  // ── Suggesting lock ───────────────────────────────────────────────────────
  // Once a document has been shared with a counterparty, EVERY change by EITHER
  // side has to be a tracked suggestion — that is the whole negotiation record,
  // and an untracked edit by the possessing side silently rewrites text the
  // other side already read. So the lock is on the document's history, not on
  // who you are: a receiver is always locked, and a creator becomes locked the
  // moment the doc has a receiver team (i.e. it has been sent at least once),
  // including after possession comes back to them.
  const hasBeenSharedWithCounterparty = !!doc?.receiverTeam?.id;
  const {
    mustSuggest,
    allowSaveVersion,
    allowPageSetup,
    allowWatermark,
    allowInsertPlaceholder,
    allowImportNewVersion,
  } = dealEditorCapabilities({ actorRole, hasBeenSharedWithCounterparty, disableWordExport });

  // Creators on a not-yet-shared document may switch modes from the toolbar.
  // The editor's `mode` prop is CONTROLLED, so a fixed value made the dropdown a
  // no-op — the parent must own the state and update it from onModeChange.
  const [chosenMode, setChosenMode] = useState<EditorMode>('editing');
  // Read-only always wins (viewing), then the suggesting lock, and only then the
  // user's toolbar choice.
  //
  // This used to seed `chosenMode` from `actorRole` via useState — which reads
  // its initializer ONCE, on the first render, while the document query is still
  // in flight. At that point `doc` is undefined, so actorRole was 'lost' and the
  // seed was 'editing'; the receiver then got `lockMode` applied to that stale
  // value and was locked INTO editing — the exact opposite of the intent. Derive
  // it from props every render instead of snapshotting a role that isn't known yet.
  const mode: EditorMode = readOnly ? 'viewing' : mustSuggest ? 'suggesting' : chosenMode;

  // ── Accept/reject permission gate ─────────────────────────────────────────
  const suggestionRows: DealSuggestionRow[] = useMemo(() => {
    const rows = (doc?.documentSuggestions ?? []) as Array<Record<string, any>>;
    return rows
      .filter((r) => r?.externalId)
      .map((r) => ({
        externalId: String(r.externalId),
        status: String(r.status ?? ''),
        authorRole: String(r.authorRole ?? ''),
      }));
  }, [doc?.documentSuggestions]);

  const suggestionGate = useMemo(
    () =>
      makeDealSuggestionGate({
        rows: suggestionRows,
        actorRole,
        actorId: user?.id ? String(user.id) : '',
        // A change with no linkable row (authored in Word, or pre-migration) is
        // resolvable by anyone who can edit — matching CKEditor's handling of
        // foreign revisions. Failing closed would strand it in the document.
        unlinkedDecision: !readOnly,
        // Once the counterparty is involved, nobody accepts their own changes.
        enforceNoSelfAccept: mustSuggest,
      }),
    [suggestionRows, actorRole, user?.id, readOnly, mustSuggest]
  );

  // ── @-mention directory: BOTH sides of the deal ───────────────────────────
  // `timelineMembers` is every person who has EVER been on the deal (both
  // teams); `members` is who's currently on it. Mentioning a removed user must
  // still resolve (their comments/suggestions live on), so we list the timeline
  // and mark departures — the same treatment the CKEditor path gives them.
  // `extraAttachments` is not on the document query the page hands down — it
  // has its own query. Without this the attachment list was always empty, so
  // File → Download PDF always took the no-attachment (print) branch.
  const { data: attachmentsData } = useQuery(queriesV2.getDocumentExtraAttachments, {
    variables: { documentId: String(documentId) },
    skip: !documentId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: dealTeamsData } = useQuery(queriesV2.getDealTeamsByTeamId, {
    skip: !dealId,
    variables: { dealId: String(dealId) },
  });
  const mentionableUsers = useMemo(() => {
    const deal = (dealTeamsData as Record<string, any>)?.getDealById;
    const timeline: Array<Record<string, any>> = deal?.timelineMembers ?? [];
    const currentIds = new Set<string>(((deal?.members ?? []) as Array<Record<string, any>>).map((m) => String(m.id)));
    const seen = new Set<string>();
    return timeline
      .filter((m) => m?.id && !seen.has(String(m.id)) && seen.add(String(m.id)))
      .map((m) => {
        const name = m.fullName || m.email || String(m.id);
        return {
          userId: String(m.id),
          handle: (m.email || m.fullName || '').split('@')[0] || String(m.id),
          display: currentIds.has(String(m.id)) ? name : `${name} *deleted*`,
        };
      });
  }, [dealTeamsData]);

  // ── Body ──────────────────────────────────────────────────────────────────
  // A deal's stored body lives on `documents.document` ({ body, body_storage_format }).
  const bodySource = (doc?.document ?? documentProp) as Record<string, any> | undefined;
  // Bumped after an import so the editor re-resolves onto the new body
  // immediately (frozen on mount otherwise, to protect live edits).
  const incomingBody: string | undefined = (bodySource as any)?.body;
  const { reloadKey, setReloadKey, expectImportedBody, importPersisted } = useImportReload(incomingBody);
  // LONG TAIL: a record the bulk migration didn't cover gets converted the
  // first time it is opened — body, comments, suggestions and versions — rather
  // than merely rendered. See useLazyDocxMigration.
  const { migrating } = useLazyDocxMigration({
    documentId,
    documentType: 'Document',
    bodySource,
    onMigrated: useCallback(async () => {
      // Re-read the record, then force `useResolvedDocxBody` off its frozen
      // pre-migration body onto the converted one.
      await apolloClient.refetchQueries({ include: ['GetDocumentById'] });
      setReloadKey((k) => k + 1);
    }, [apolloClient]),
  });

  const {
    document: editorDocument,
    documentBuffer: storedBuffer,
    resolving,
  } = useResolvedDocxBody<EditorDocument, EditorDocumentBuffer>(bodySource, reloadKey);

  // ── Private comment restoration ───────────────────────────────────────────
  // The stored body carries private comments only as content-free
  // `propdocs-pc-<id>` bookmarks. Rebuild the ones this user is entitled to see
  // BEFORE the editor parses the bytes, so the sidebar, anchoring and replies
  // all work through the ordinary comment path with no special-casing.
  // Anything not returned by the server stays an inert bookmark.
  const [hydratedBuffer, setHydratedBuffer] = useState<EditorDocumentBuffer | undefined>(undefined);
  const [hydrating, setHydrating] = useState(false);
  /** w:ids of the private comments restored for THIS viewer — see below. */
  const [restoredPrivateIds, setRestoredPrivateIds] = useState<string[]>([]);

  useEffect(() => {
    if (!storedBuffer) {
      setHydratedBuffer(undefined);
      return;
    }
    let cancelled = false;
    setHydrating(true);

    (async () => {
      try {
        const restorable = await privateCommentSync.fetchRestorablePrivateComments();
        if (cancelled) return;
        // Hand the ids to the editor so the rebuilt comments keep their Private
        // badge — nothing in the .docx itself records that they're internal.
        setRestoredPrivateIds(restorable.map((c) => String(c.id)));
        if (!restorable.length) {
          setHydratedBuffer(storedBuffer);
          return;
        }
        const { rehydratePrivateCommentsDocx } = await import('@propdocs-editor/review');
        const next = await rehydratePrivateCommentsDocx(storedBuffer as ArrayBuffer, restorable);
        if (!cancelled) setHydratedBuffer(next as EditorDocumentBuffer);
      } catch {
        // Restoration is additive — on failure open the document as stored
        // rather than blocking it.
        if (!cancelled) setHydratedBuffer(storedBuffer);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `privateCommentSync` is stable per documentId; re-running on every render
    // would remount the editor and destroy the live edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedBuffer]);

  const documentBuffer = hydratedBuffer;

  // ── Autosave ──────────────────────────────────────────────────────────────
  // Serialize via the editor's OWN save() → .docx bytes, and persist with
  // storageFormat 'docx' (the backend stores it verbatim and stamps
  // body_storage_format='docx'). save() is read-only on the live view, so typing
  // continues uninterrupted.
  const persist = useCallback(async () => {
    // Save when the user may edit the BODY *or* comment on it. Gating purely on
    // `readOnly` (which folds in `canEdit`) meant a reviewer who can comment but
    // not edit had their comments silently dropped — comments are stored in the
    // same .docx body, so they still need a save. The lifecycle lock
    // (signing/executed) and compare mode still block everything.
    const mayPersist = statusAllowsEditing && editorMode !== 'compare' && (canEdit || canComment);
    if (!documentId || !mayPersist) {
      // Loud in dev: a silent no-op here is indistinguishable from "autosave is
      // broken", which is exactly how this went unnoticed. Says WHICH gate shut
      // it off so the next person doesn't have to bisect the boolean.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[docx autosave] skipped — no save will happen', {
          documentId,
          statusAllowsEditing,
          editorMode,
          canEdit,
          canComment,
          documentStatus,
          hasCurrentTeamPossession,
        });
      }
      return;
    }
    if (savingRef.current) {
      dirtyAgainRef.current = true;
      return;
    }
    savingRef.current = true;
    try {
      // Persist private comment TEXT to the access-controlled comments table
      // BEFORE serializing. The save strips private threads out of the body
      // (the body is shared with the counterparty), so if this ran after — or
      // failed silently — the text would be gone with nothing holding it.
      // See useDocxPrivateCommentSync.
      await privateCommentSync.syncPrivateComments(editorRef.current?.getComments?.());

      const buffer = await editorRef.current?.save?.();
      if (buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
        await updateDocument({
          documentId: String(documentId),
          document: { body: btoa(binary) },
          storageFormat: 'docx',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)(apolloClient);
        onSaved?.();
      }
    } catch (err) {
      // Autosave failure is non-fatal to the live doc, but MUST NOT be silent —
      // a swallowed error here means the user keeps typing into a document that
      // is no longer being persisted. Surface it so the failure is observable.
      // eslint-disable-next-line no-console
      console.error('[docx autosave] FAILED — changes are not being saved', err);
      onAutosaveError?.(err);
    } finally {
      savingRef.current = false;
      if (dirtyAgainRef.current) {
        dirtyAgainRef.current = false;
        void persist();
      }
    }
  }, [
    apolloClient,
    documentId,
    statusAllowsEditing,
    editorMode,
    canEdit,
    canComment,
    documentStatus,
    hasCurrentTeamPossession,
    onAutosaveError,
    onSaved,
    privateCommentSync,
  ]);

  // ── Save version ──────────────────────────────────────────────────────────
  // Flush the live body first, THEN snapshot — otherwise the version captures
  // the last autosaved state and silently omits edits made since.
  const [saveVersionMutation] = useMutation(mutations.saveDocumentVersion);
  const handleSaveVersion = useCallback(async () => {
    if (!documentId) return;
    const name = window.prompt('Name this version (optional):');
    if (name === null) return; // cancelled
    try {
      await persist();
      const { data } = await saveVersionMutation({
        variables: {
          documentId: String(documentId),
          documentType: 'Document',
          name: name.trim() || null,
          event: 'manual',
        },
      });
      const errs = (data as any)?.saveDocumentVersion?.errors ?? [];
      if (errs.length) {
        notifyError({ message: errs[0], pinned: true });
      } else {
        notifySuccess({ message: 'Version saved.' });
      }
    } catch {
      notifyError({ message: 'Could not save this version. Please try again.', pinned: true });
    }
  }, [documentId, persist, saveVersionMutation, notifyError, notifySuccess]);

  // ── Import new version (File → "Import new version…") ─────────────────────
  // Snapshot the OUTGOING body BEFORE the incoming file replaces it — that is
  // the whole point of an explicit import: the version you are replacing has to
  // remain in history. Then hand the file to the editor's own loader.
  const handleImportNewVersion = useCallback(
    async (file: File) => {
      if (documentId) {
        try {
          await persist();
          await saveVersionMutation({
            variables: {
              documentId: String(documentId),
              documentType: 'Document',
              name: `Before import — ${new Date().toLocaleString()}`,
              event: 'import',
            },
          });
        } catch {
          // A failed snapshot must not block the import, but the user should
          // know history wasn't captured.
          notifyError({
            message: "Couldn't save a version before importing. The import will continue.",
          });
        }
      }
      // Persist the imported .docx as the new body, then refetch so the editor
      // remounts on it. Writing to the backend (rather than poking the live
      // view) keeps ONE source of truth — `useResolvedDocxBody` already derives
      // the editor's content from the stored body.
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
        const newBody = btoa(binary);
        // Remember the imported body so the editor re-resolves onto it the moment
        // the refetched query carries it (no leave/return).
        expectImportedBody(newBody);
        await updateDocument({
          documentId: String(documentId),
          document: { body: newBody },
          storageFormat: 'docx',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)(apolloClient);
        // Evict the cached document so the page's query re-fetches the new
        // body; `useResolvedDocxBody` then remounts the editor on it.
        apolloClient.cache.evict({ id: `Document:${documentId}` });
        apolloClient.cache.gc();
        await apolloClient.refetchQueries({ include: 'active' });
        importPersisted();
        notifySuccess({ message: 'New version imported.' });
      } catch {
        notifyError({ message: 'Could not import that file. Please try again.', pinned: true });
      }
    },
    [documentId, persist, saveVersionMutation, notifyError, notifySuccess, apolloClient, expectImportedBody, importPersisted]
  );

  // ── Download PDF ──────────────────────────────────────────────────────────
  // Two genuinely different operations behind one menu item:
  //   * No attachments  → PRINT. Instant, client-side, no round trip. This is
  //     the overwhelmingly common case.
  //   * Has attachments → ask. Merging means concatenating rendered PDFs,
  //     which the browser cannot do, so it goes to the server (HexaPDF).
  // Always queuing a server render made the common case slow for no benefit.
  const pdfAttachments = useMemo<PdfAttachment[]>(() => {
    const rows = (attachmentsData?.getDocumentById?.extraAttachments ?? []) as Array<Record<string, any>>;
    return rows.map((a, i) => ({
      id: String(a?.id),
      // `attachment` is a { filename, url } object, NOT a string — rendering it
      // directly crashed React ("Objects are not valid as a React child").
      label:
        a?.linkedDocument?.title ||
        a?.attachment?.filename ||
        (typeof a?.attachment === 'string' ? a.attachment : null) ||
        `Attachment ${i + 1}`,
    }));
  }, [attachmentsData]);

  const printDocumentOnly = useCallback(() => {
    setPdfModalOpen(false);
    editorRef.current?.print?.();
  }, []);

  const queueMergedPdf = useCallback(
    async (selectedIds: string[]) => {
      setPdfModalOpen(false);
      if (!documentId || isExportingPdf) return;
      try {
        const { data } = await exportPdfMutation({
          variables: { documentId: String(documentId), selectedAttachmentIds: selectedIds },
        });
        const result = (data as any)?.exportDocumentPdf;
        if (result?.queued) {
          startPdfPolling({
            id: String(documentId),
            type: 'document',
            title: doc?.title || 'document',
          });
        } else {
          notifyError({ message: result?.errors?.[0] || 'PDF export failed', pinned: true });
        }
      } catch {
        notifyError({ message: 'Failed to start PDF export. Please try again.', pinned: true });
      }
    },
    [documentId, isExportingPdf, exportPdfMutation, startPdfPolling, doc?.title, notifyError]
  );

  const handleDownloadPdf = useCallback(() => {
    if (pdfAttachments.length === 0) {
      printDocumentOnly();
      return;
    }
    setPdfModalOpen(true);
  }, [pdfAttachments.length, printDocumentOnly]);

  // After the relational rows change, pull the fresh `documentSuggestions` into
  // DocumentContext — that's what the send-for-signing dropdown reads to decide
  // if it's blocked (sendForSigningContainer: state.documentSuggestions filtered
  // to pending). Without this the gate wouldn't update until a full page reload.
  const refreshDealSuggestions = useCallback(async () => {
    if (!documentId) return;
    try {
      const response = await getDocumentById(String(documentId))(apolloClient);
      if (response) updateDocumentState(documentStateFromResponse(response));
    } catch {
      // Non-fatal: the gate keeps its last state; next change retries.
    }
  }, [documentId, apolloClient, updateDocumentState]);

  // Insert an interactive checkbox at the cursor (deal Insert menu). Deals can
  // add checkboxes to their docs just like templates; placeholder AUTHORING
  // stays template-only (deals FILL placeholders via the sidebar, not author them).
  const insertCheckbox = useCallback(() => {
    const view = editorRef.current?.getEditorRef?.()?.getView?.() as any;
    if (!view) return;
    const { schema, tr } = view.state;
    const node = buildCheckboxNode(schema);
    if (node) view.dispatch(tr.replaceSelectionWith(node, false).scrollIntoView());
    view.focus();
  }, []);

  const handleChange = useCallback(() => {
    onChange?.();
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      void persist();
      // Reconcile the relational suggestion rows from the same live PM state the
      // autosave captured. Debounced with the save so a burst of edits syncs once.
      // Body AND header/footer — a header edit lives in its own PM view, so
      // reading the body state alone reported zero pending changes.
      const count = await suggestionSync.sync(editorRef.current?.getTrackedChanges?.() ?? null);
      // Refresh the deal's suggestion state only when the pending count moved —
      // a text-only edit (no suggestion added/resolved) shouldn't refetch.
      if (count !== lastSuggCountRef.current) {
        lastSuggCountRef.current = count;
        void refreshDealSuggestions();
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [persist, onChange, suggestionSync, refreshDealSuggestions]);

  // A COMMENT CHANGE IS A DOCUMENT CHANGE — save it on its own.
  //
  // Autosave only ran off `handleChange`, which the editor fires for content
  // edits. Adding a comment does dispatch a mark transaction, but relying on
  // that coupling meant a comment's durability depended on a text edit landing
  // too. It matters most for PRIVATE comments, whose text is mirrored to
  // `document_comments` immediately while the anchor the mirror needs to
  // reattach to only reaches the body on a save — orphan the anchor and the
  // comment is unrecoverable, not merely late.
  // Signature of the comment set as the document was LOADED. The editor emits a
  // change for the load itself (it seeds its list from the body), and saving on
  // that would rewrite every document merely because someone opened it.
  const commentSigRef = useRef<string | null>(null);
  const handleCommentsChange = useCallback(
    (next: { id: string | number; content?: unknown; done?: boolean }[]) => {
      const sig = next.map((c) => `${c.id}:${c.done ? 1 : 0}:${JSON.stringify(c.content)}`).join('|');
      const baseline = commentSigRef.current;
      commentSigRef.current = sig;
      if (baseline === null || baseline === sig) return; // load, or no real change
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        void persist();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [persist]
  );

  // Unmount must FLUSH the pending autosave, not drop it.
  //
  // Clearing the timer discarded up to `AUTOSAVE_DEBOUNCE_MS` of work every time
  // the editor came down (navigation, import, a loading flip). For a private
  // comment that loss is unrecoverable: the mirror row is already written, but
  // the anchor that restoration needs only exists in the body, which the
  // dropped save was going to write. `persist` reads the live view, so firing it
  // here captures the same state the timer would have.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(
    () => () => {
      if (!saveTimer.current) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      void persistRef.current();
    },
    []
  );

  // ── Restricted editing ────────────────────────────────────────────────────
  // The DEAL enforces the template author's restriction: everything is locked
  // except the regions marked editable ('locked'), unlike the template editor
  // which is fully editable so the author can MARK those regions ('template').
  // Built with the doc's context so header/footer views (created lazily, and
  // never reached by the body's setRestrictedContext) start locked too.
  const externalPlugins = useMemo(
    () => [restrictedEditingPlugin(isRestrictedEditingEnabled ? 'locked' : 'off'), placeholderSyncPlugin()],
    [isRestrictedEditingEnabled]
  );
  const restrictContext = isRestrictedEditingEnabled ? 'locked' : 'off';
  const applyRestrictedContext = useCallback(() => {
    const view = editorRef.current?.getEditorRef?.()?.getView?.() as any;
    if (view) setRestrictedContext(view, restrictContext);
  }, [restrictContext]);
  // onReady fires once at view creation; this also re-applies if the flag flips.
  useEffect(() => {
    applyRestrictedContext();
  }, [applyRestrictedContext]);

  // ── Signal "editor loaded" to the deal page chrome ────────────────────────
  // `DocumentContext.isEditorLoaded` gates the LEFT SIDENAV and the SEND
  // dropdown (see useSendDropdownLogic + DocumentPage). Only the CKEditor
  // components ever set it, so a docx-native deal left both hidden forever.
  // Set it once the view exists, and clear it on unmount so navigating to
  // another document doesn't inherit a stale "loaded" state.
  const handleEditorViewReady = useCallback(
    (view: unknown) => {
      setRestrictedContext(view as any, restrictContext);
      updateDocumentState({ isEditorLoaded: true });
    },
    [restrictContext, updateDocumentState]
  );
  useEffect(
    () => () => {
      updateDocumentState({ isEditorLoaded: false });
    },
    [updateDocumentState]
  );

  // Force the debounced autosave to land NOW and reconcile the relational
  // suggestion rows. Deal-flow transitions call this through
  // `flushActiveEditor` before they mutate — see DocxNativeEditorHandle.
  const flushPendingChanges = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    await persist();
    const pendingSuggestions = await suggestionSync.sync(editorRef.current?.getTrackedChanges?.() ?? null);
    lastSuggCountRef.current = pendingSuggestions;
    return { pendingSuggestions };
  }, [persist, suggestionSync]);

  // Keep the registered handle's flush pointing at the CURRENT closure without
  // re-registering (which would churn every subscriber on each render).
  const flushRef = useRef(flushPendingChanges);
  flushRef.current = flushPendingChanges;

  // Register so sibling deal panels can reach this editor (same bridge the
  // template editor uses for its sidebars).
  useEffect(() => {
    if (!documentId) return;
    const handle = makeHandleFromRef(
      editorRef,
      () => () => {},
      () => {}
    );
    handle.flushPendingChanges = () => flushRef.current();
    return registerDocxNativeEditor(String(documentId), handle);
  }, [documentId]);

  // Live accessor for the body EditorView — lets the bridge read whether the
  // caret sits in an editable region (restricted deals gate insert on that).
  const getBodyView = useCallback(
    () => (editorRef.current?.getEditorRef?.()?.getView?.() as EditorView | null) ?? null,
    []
  );

  // ── Clause drag-and-drop (same native-HTML5 system as the template editor) ──
  // The deal's Clauses panel renders the SAME drag source (CLAUSE_DND_MIME
  // payload on dragstart); only this drop-target wiring was missing, so drops
  // were refused (no preventDefault → no copy cursor, nothing on drop).
  const insertClause = useCallback(
    async (payload: ClauseDndPayload): Promise<boolean> => {
      const { format, body } = payload;
      if (!body) return false;
      let buffer: Uint8Array;
      if (format === 'docx') {
        const { base64ToBytes } = await import('./clauseBody');
        buffer = base64ToBytes(body);
      } else {
        if (!body.trim()) return false;
        const { convertHtmlToDocx } = await import('../../../../utils/docx-migration-v2');
        buffer = (await convertHtmlToDocx(body, window.DOMParser)).buffer;
      }
      const inserted = (await editorRef.current?.insertDocxFragment?.(buffer)) ?? false;
      if (inserted) handleChange(); // trigger autosave for the new content
      return inserted;
    },
    [handleChange]
  );

  // Visible drop-caret ("|") at the target point — the editor's own caret is
  // hidden while unfocused (the drag source holds focus during a drag).
  const [dropCaret, setDropCaret] = useState<{ top: number; left: number; height: number } | null>(null);

  const moveCaretToPoint = useCallback((clientX: number, clientY: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const paged = editorRef.current?.getEditorRef?.() as any;
    const pos = paged?.positionFromCoords?.(clientX, clientY);
    if (typeof pos !== 'number') return;
    paged?.setSelection?.(pos);
    const caretEl = window.document.querySelector<HTMLElement>('[data-testid="caret"]');
    if (caretEl) {
      const r = caretEl.getBoundingClientRect();
      if (r.height) setDropCaret({ top: r.top, left: r.left, height: r.height });
    }
  }, []);

  const onClauseDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes(CLAUSE_DND_MIME)) return;
      e.preventDefault(); // required for the drop to be allowed + copy cursor
      moveCaretToPoint(e.clientX, e.clientY);
      // RESTRICTED deals: only editable regions accept content. Show the
      // no-drop cursor over locked body (the lock would reject the insert).
      const insertable = isCursorInsertable(getBodyView());
      e.dataTransfer.dropEffect = insertable ? 'copy' : 'none';
      if (!insertable) setDropCaret(null);
    },
    [moveCaretToPoint, getBodyView]
  );

  const onClauseDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only clear when the pointer actually leaves the container (not when moving
    // between inner children — those fire dragleave with relatedTarget inside).
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropCaret(null);
  }, []);

  const onClauseDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const payload = decodeClauseDnd(e.dataTransfer.getData(CLAUSE_DND_MIME));
      if (!payload) return; // not a clause drop
      e.preventDefault();
      setDropCaret(null);
      moveCaretToPoint(e.clientX, e.clientY); // insert at the drop point
      if (!isCursorInsertable(getBodyView())) return; // locked region — refuse
      void insertClause(payload);
    },
    [moveCaretToPoint, insertClause, getBodyView]
  );

  // Wire the (kept) CKEditor-era placeholder sidebar to the docx editor: enables
  // the sidebar "+"/insert, and applies filled values into the doc body in
  // realtime. The sidebar persists current_value relationally itself.
  const { announceCanInsert } = useDealPlaceholderBridge(
    documentId ? String(documentId) : undefined,
    handleChange,
    getBodyView
  );

  // On a RESTRICTED (locked) deal, inserting content controls is only allowed
  // inside an editable region. Track that so the Insert menu's Placeholder /
  // Checkbox items disable when the caret is in the locked body (and the sidebar
  // "+" tracks it via the bridge's announce). On an unrestricted deal, always
  // insertable. Recomputed on every selection change.
  const [canInsertHere, setCanInsertHere] = useState(!isRestrictedEditingEnabled);
  const handleSelectionChange = useCallback(() => {
    const insertable = isCursorInsertable(getBodyView());
    setCanInsertHere(insertable);
    // Keep the sidebar "+" in lockstep with the menu items.
    announceCanInsert();
  }, [getBodyView, announceCanInsert]);

  // Clicking a placeholder IN THE DOCUMENT opens + focuses it in the placeholder
  // SIDEBAR (deal placeholders are FILLED in the sidebar, not via an in-doc
  // popup). Fire the same `focusPlaceholderInSidebar` window event the CKEditor
  // path used — the deal's placeholder sidebar already listens for it (switches
  // to the This-Document tab + highlights the clicked field by uuid).
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const span = target?.closest?.('[data-sdt-tag="propdocs:placeholder@1"]') as HTMLElement | null;
      if (!span) return;
      // OPEN the placeholder sidenav if it's collapsed (the deal sidenav listens
      // for this event and un-collapses + activates the placeholder panel).
      window.dispatchEvent(new CustomEvent('openPlaceholdersSidebar'));
      // Resolve the clicked placeholder from the registry: map the painted span →
      // its PM pos → the placeholder record (id + name).
      const handle = getDocxNativeEditor(String(documentId));
      const pmStart = Number(span.dataset.pmStart);
      const list = handle?.listPlaceholders() ?? [];
      // The clicked span sits INSIDE the placeholder SDT, so the enclosing
      // placeholder is the last one whose pos precedes the click.
      const match = Number.isFinite(pmStart) ? ([...list].reverse().find((p) => p.pos <= pmStart) ?? list[0]) : list[0];
      // Fire the FOCUS event on the NEXT tick: opening a collapsed sidebar mounts
      // DocumentFieldsList, whose `focusPlaceholderInSidebar` listener attaches on
      // mount — firing synchronously would miss it (the sidebar opens but the
      // clicked field doesn't expand). The sidebar highlights by placeholder
      // UUID; MIGRATED placeholders carry no uuid in the body (only the alias),
      // so we also send `name` for a fallback match.
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('focusPlaceholderInSidebar', {
            detail: { placeholderId: match?.id ?? null, placeholderName: match?.name ?? null },
          })
        );
      }, 60);
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [documentId]);

  // MOUNT gate, never a render gate.
  //
  // `hydrating` joins it so the editor never mounts on the pre-restoration bytes
  // and then remounts — that would discard the freshly parsed comments.
  //
  // But returning null AFTER the editor is up UNMOUNTS it, and the editor holds
  // real unsaved state in React: the comment list, the suggestion list and the
  // undo stack. `documentLoading` is the page's `getDocumentById` query, which
  // re-runs on refetch — so a routine background refetch silently destroyed
  // every comment made since the last successful save. A PRIVATE comment died
  // permanently that way: its text is mirrored to `document_comments` the moment
  // it's created, but its ANCHOR only reaches the body on the next save, and
  // restoration needs the anchor. The row survived with nothing to attach to,
  // which is exactly "the comment is there, then it's gone after reload".
  //
  // Once mounted the editor owns the document; later loading flips are ignored.
  const gateOpen = !(documentLoading || resolving || hydrating || migrating);
  if (gateOpen) hasMountedRef.current = true;
  if (!hasMountedRef.current) return null;

  return (
    // `is-restricted-active` scopes the restricted-region highlight so locked
    // documents visibly show which areas are editable (same as the template).
    // The explicit 100% height/width is REQUIRED: this wrapper sits inside the
    // deal page's flex column, and without it the editor's height chain
    // collapses — which kills page scrolling and the surrounding chrome.
    <div
      ref={containerRef}
      className={isRestrictedEditingEnabled ? 'is-restricted-active' : undefined}
      // `position: relative` anchors the Compare overlay below; without it the
      // absolutely-positioned panel would escape to a distant ancestor.
      style={{ height: '100%', width: '100%', position: 'relative' }}
      // Clause drag-and-drop (same native-HTML5 system as the template editor).
      // `onDragOver` MUST preventDefault or the browser refuses the drop.
      onDragOver={onClauseDragOver}
      onDrop={onClauseDrop}
      onDragLeave={onClauseDragLeave}
      onDragEnd={() => setDropCaret(null)}
    >
      {/* Drop-caret: shows exactly where a dragged clause will land (the
          editor's own caret is hidden while it isn't focused). */}
      {dropCaret && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: dropCaret.top,
            left: dropCaret.left,
            width: 2,
            height: dropCaret.height,
            background: '#006c90',
            zIndex: 6000,
            pointerEvents: 'none',
            animation: 'propdocs-drop-caret-blink 1s steps(2, start) infinite',
          }}
        />
      )}
      <style>{'@keyframes propdocs-drop-caret-blink{50%{opacity:0}}'}</style>
      <DocxEditor
        ref={editorRef}
        {...(editorDocument ? { document: editorDocument } : { documentBuffer })}
        mode={mode}
        // Both sides work in SUGGESTING mode only once the document has been
        // shared — their edits must be reviewable, never applied directly.
        // `mode` alone was merely the DEFAULT: the toolbar's mode dropdown let
        // the user switch to 'editing' and write straight into the document.
        lockMode={mustSuggest}
        onModeChange={(next) => {
          // Ignore the editor reporting 'viewing' back to us — that is derived
          // from readOnly, not a user choice. Persist only editing/suggesting.
          if (next === 'editing' || next === 'suggesting') setChosenMode(next);
        }}
        readOnly={readOnly}
        author={author}
        documentStatus={lifecycleStatus}
        suggestionGate={suggestionGate}
        mentionableUsers={mentionableUsers}
        allowPrivateComments={canComment}
        // SECURITY: restored private comments carry no `isPublic` flag (OOXML
        // has no such concept), so the editor's own fallback would classify
        // them public and write their text into the body the counterparty
        // downloads. The hook knows which ids came out of the private mirror.
        getPrivateCommentIds={() => privateCommentSync.getPrivateCommentIds(editorRef.current?.getComments?.())}
        // Same knowledge, applied at LOAD: without it a restored comment shows
        // as an ordinary comment with no Private badge.
        privateCommentIds={restoredPrivateIds}
        onChange={handleChange}
        // Comments are state the editor holds until a save writes them into the
        // body — schedule one whenever the set changes. See handleCommentsChange.
        onCommentsChange={handleCommentsChange}
        // Recompute insert-affordance state as the caret moves (restricted deals
        // only allow inserting content controls inside an editable region).
        onSelectionChange={handleSelectionChange}
        // Restricted docs: grey the formatting controls out when the caret sits
        // in locked content. The lock already REJECTS such edits, but a silently
        // dead Bold button reads as broken — Word/Docs disable instead.
        formattingDisabled={isRestrictedEditingEnabled && !canInsertHere}
        onDownloadPdf={handleDownloadPdf}
        // Named versions are the OWNER's record of the document's history —
        // the counterparty gets no say in what the deal's version list says.
        onSaveVersion={allowSaveVersion ? handleSaveVersion : undefined}
        // File → Open ("import new version") REPLACES the whole body; it is not
        // an import-as-suggestions path. In a counterparty's hands that is a way
        // to swap the entire agreement with no tracked changes to review, which
        // defeats the suggesting lock — so it stays with the document's owner.
        //
        // BOTH flags, and they must move together: `onOpen` only customizes what
        // happens to the CHOSEN file, while `showFileOpen` is what removes the
        // menu entry and the Ctrl+O shortcut. Withholding `onOpen` alone leaves
        // the picker in place and falls back to the editor's BUILT-IN local
        // load — strictly worse than the host path it was meant to block.
        showFileOpen={allowImportNewVersion}
        onOpen={allowImportNewVersion ? handleImportNewVersion : undefined}
        // Deal Insert menu: add an interactive checkbox (same as the template
        // editor). Placeholder authoring stays template-only. On a RESTRICTED
        // deal, both are gated on the caret being in an editable region — passing
        // `undefined` disables the menu item (the editor removes items whose
        // handler is absent), matching the sidebar "+" state.
        onInsertCheckbox={canInsertHere ? insertCheckbox : undefined}
        // Insert > Placeholder opens the deal placeholder sidebar's create modal
        // (which mints the DB row, then fires insertPlaceholderAtCursor → our
        // bridge inserts the SDT). Keeps the deal's create UX in the sidebar.
        // Placeholders are authored by the document's owner: they mint a DB row
        // on the deal and define what the OTHER side is asked to fill in, so a
        // counterparty never creates them.
        onInsertPlaceholder={
          canInsertHere && allowInsertPlaceholder
            ? () => {
                window.dispatchEvent(new CustomEvent('openPlaceholdersSidebar'));
                window.dispatchEvent(new CustomEvent('openCreatePlaceholderModal'));
              }
            : undefined
        }
        // Applies the restriction + tells the deal page the editor is loaded
        // (which reveals the left sidenav and the Send dropdown).
        onEditorViewReady={handleEditorViewReady}
        externalPlugins={externalPlugins}
        // Inherited from the template's editor configuration. On a DEAL document
        // this binds everyone — creator included: the template author turned
        // Word export off for the document itself, not for one audience.
        disableDocxExport={disableWordExport}
        downloadFileName={doc?.title}
        // Page geometry and watermarks are document-wide statements the OWNER
        // makes. A counterparty proposes text; they don't re-lay-out the page
        // or stamp it. (Hides File → Page setup and Insert → Watermark, and
        // makes the ruler's margin handles inert.)
        allowPageSetup={allowPageSetup}
        allowWatermark={allowWatermark}
        headerLocked={headerLocked}
        footerLocked={footerLocked}
        // The deal ENFORCES restrictions; marking new editable regions is a
        // template-authoring action, so it stays off here.
        makeEditableEnabled={false}
        showToolbar
        // The ruler is an editing affordance; an executed (terminal, locked)
        // deal doc is view-only, so hide it there — matches how a signed
        // document has no editing chrome.
        showRuler={lifecycleStatus !== 'executed'}
        // The document outline is likewise an editing/navigation affordance we
        // don't want on the final, view-only executed document.
        showOutlineButton={lifecycleStatus !== 'executed'}
        // Zoom is hidden — zooming mis-aligns overlays (comments, caret, H/F chrome).
        showZoomControl={false}
        // PropDocs supplies its own document chrome above the editor.
        renderLogo={() => null}
        onCompareVersions={onCompareVersions}
      />
      {/* Only reachable when the document HAS attachments — the no-attachment
          path prints directly without ever opening this. */}
      {pdfModalOpen && (
        <DownloadPdfModal
          attachments={pdfAttachments}
          onCancel={() => setPdfModalOpen(false)}
          onDocumentOnly={printDocumentOnly}
          onMerge={queueMergedPdf}
        />
      )}
    </div>
  );
};

export default DocxNativeDocumentEditorContainer;
